import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReportTemplate from '../../components/reports/ReportTemplate';
import { ROUTES } from '../../consts/general';
import { INBOUND_GET_READY } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import { formatDate, formatDateTime, formatNumber } from '../warehouse/format';
import useWarehouseEditAccess from '../warehouse/useWarehouseEditAccess';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const emptyPlan = { planDate: null, generatedAt: null, droppedCount: 0, summary: {}, entries: [], planDates: [] };

const formatDay = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  return match ? `${match[2]}/${match[3]}/${match[1]}` : formatDate(value);
};

const stateLabel = (entry) => {
  if (entry.landingSoon) return `Landing soon · ETA ${formatDay(entry.eta)}`;
  if (entry.overdue) return `Overdue · est. ${formatDay(entry.eta)}`;
  if (entry.eta) return `ETA ${formatDay(entry.eta)}`;
  return 'No ETA';
};

const skuSummary = (rows) => {
  const skus = [...new Set((rows || []).map((row) => row.sku).filter(Boolean))];
  if (skus.length === 1) {
    const row = (rows || [])[0];
    return row.name ? `${row.name} (${row.sku})` : row.sku;
  }
  return `${formatNumber(skus.length)} SKUs`;
};

const SUMMARY_TILES = [
  ['Landing soon', (plan) => plan.summary?.landingSoon],
  ['Overdue', (plan) => plan.summary?.overdue],
  ['Later', (plan) => plan.summary?.later],
  ['Plan unavailable', (plan) => plan.summary?.unavailable],
  ['Total units', (plan) => plan.summary?.totalUnits],
];

const InboundGetReadyReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);
  const canEdit = useWarehouseEditAccess();
  const date = searchParams.get('date') || '';
  const [plan, setPlan] = useState(emptyPlan);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState('');

  const load = useCallback(async () => {
    const { signal, requestId } = beginRequest();
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams(date ? { date } : {});
      const response = await fetch(`/api/v1/reports/inbound-get-ready?${query}`, { headers: authHeaders(), signal });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to load the put-away plan');
      if (!isLatest(requestId)) return;
      setPlan(json);
    } catch (requestError) {
      if (requestError?.name === 'AbortError' || !isLatest(requestId)) return;
      setError(requestError.message);
    } finally {
      if (isLatest(requestId)) setLoading(false);
    }
  }, [beginRequest, isLatest, date]);

  useEffect(() => {
    if (initialReady) load();
  }, [initialReady, load]);

  const updateParams = (next) => {
    const params = { ...Object.fromEntries(searchParams.entries()), ...next };
    Object.keys(params).forEach((key) => { if (!params[key]) delete params[key]; });
    setSearchParams(params);
  };

  const downloadPdf = async () => {
    setDownloading(true);
    setActionError('');
    try {
      const query = new URLSearchParams(plan.planDate ? { date: plan.planDate } : {});
      const response = await fetch(`/api/v1/reports/inbound-get-ready/export?${query}`, { headers: authHeaders() });
      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        throw new Error(json.error || 'Failed to export the put-away plan');
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const filename = disposition.match(/filename="?([^"]+)"?/i)?.[1] || `inbound-get-ready-${plan.planDate}.pdf`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setDownloading(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    setActionError('');
    try {
      const response = await axios.post('/api/v1/reports/inbound-get-ready/run');
      setPlan(response.data);
      updateParams({ date: '' });
    } catch (requestError) {
      setActionError(requestError.response?.data?.error || 'Unable to generate the put-away plan');
    } finally {
      setRunning(false);
    }
  };

  const exportButton = (
    <div className="flex items-center gap-2">
      {canEdit && (
        <button type="button" onClick={runNow} disabled={running} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40">
          {running ? 'Asking KIP for put-away plans…' : 'Generate now'}
        </button>
      )}
      <button type="button" onClick={downloadPdf} disabled={downloading || !plan.planDate || !(plan.entries || []).length} className="rounded-md bg-black px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-40">
        {downloading ? 'Preparing PDF…' : 'Download PDF'}
      </button>
    </div>
  );

  return (
    <ReportTemplate
      reportId={INBOUND_GET_READY}
      title="Inbound — Get Ready"
      description="Put-away plan for shipped inbound POs — where each SKU goes when it lands, with the same PDF the inbound Slack digest sends."
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      isLoading={loading}
      error={error}
      exportButton={exportButton}
      renderChildrenOnly
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-gray-600">Plan date</span>
            <select value={date} onChange={(event) => updateParams({ date: event.target.value })} className="h-8 rounded border border-gray-300 bg-white px-2 text-sm">
              {(plan.planDates || []).length === 0 && <option value="">No plans generated</option>}
              {(plan.planDates || []).map((value, index) => <option key={value} value={index === 0 ? '' : value}>{formatDay(value)}{index === 0 ? ' (latest)' : ''}</option>)}
              {date && !(plan.planDates || []).includes(date) && <option value={date}>{formatDay(date)} (no plan)</option>}
            </select>
          </label>
          <span className="text-gray-500">
            {plan.generatedAt ? `Generated ${formatDateTime(plan.generatedAt)}` : 'Generated by the inbound digest or Generate now'}
          </span>
          {plan.droppedCount > 0 && <span className="text-gray-500">+{plan.droppedCount} more shipped POs not planned this run</span>}
          {actionError && <span className="text-red-600">{actionError}</span>}
        </div>
        <div className="grid gap-3 md:grid-cols-5">
          {SUMMARY_TILES.map(([label, pick]) => (
            <div key={label} className="rounded border bg-white p-3">
              <div className="text-[10px] font-medium uppercase text-gray-500">{label}</div>
              <div className="mt-1 text-lg font-semibold">{formatNumber(pick(plan) ?? 0)}</div>
            </div>
          ))}
        </div>
        {!plan.planDate || (plan.entries || []).length === 0 ? (
          <div className="rounded border bg-white px-6 py-12 text-center text-sm text-gray-500">
            No put-away plan yet — click Generate now or wait for the next inbound digest.
          </div>
        ) : (
          (plan.entries || []).map((entry) => {
            const planOk = entry.plan?.status === 'ok';
            const governance = (entry.plan?.governanceRefs || [])
              .map((ref) => (typeof ref === 'string' ? ref : ref?.title))
              .filter(Boolean);
            return (
              <div key={entry.poNumber} className="rounded border bg-white">
                <div className="border-b px-4 py-3 text-sm font-medium">
                  PO {entry.poNumber} — {entry.vendorName || 'Unknown vendor'} — {skuSummary(entry.rows)} — {formatNumber(entry.units)} units · {formatNumber(entry.boxes)} boxes · {stateLabel(entry)}
                </div>
                <div className="grid gap-4 p-4 md:grid-cols-2">
                  <div>
                    <h3 className="mb-1 text-xs font-semibold uppercase text-gray-500">Inbound</h3>
                    <table className="w-full text-left text-xs">
                      <thead className="bg-gray-50"><tr>{['SKU', 'Product', 'Units', 'Boxes'].map((label) => <th key={label} className="px-2 py-1.5">{label}</th>)}</tr></thead>
                      <tbody>
                        {(entry.rows || []).map((row) => (
                          <tr key={row.sku} className="border-t">
                            <td className="px-2 py-1.5">{row.sku}</td>
                            <td className="px-2 py-1.5">{row.name || '—'}</td>
                            <td className="px-2 py-1.5">{formatNumber(row.units)}</td>
                            <td className="px-2 py-1.5">{formatNumber(row.boxes)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div>
                    <h3 className="mb-1 text-xs font-semibold uppercase text-gray-500">Where it goes</h3>
                    {planOk ? (
                      <table className="w-full text-left text-xs">
                        <thead className="bg-gray-50"><tr>{['SKU', 'From', 'To', 'Qty'].map((label) => <th key={label} className="px-2 py-1.5">{label}</th>)}</tr></thead>
                        <tbody>
                          {(entry.plan?.moves || []).map((move, index) => (
                            <tr key={`${move.sku}-${index}`} className="border-t">
                              <td className="px-2 py-1.5">{move.sku}</td>
                              <td className="px-2 py-1.5">{move.from}</td>
                              <td className="px-2 py-1.5">{move.to}</td>
                              <td className="px-2 py-1.5">{formatNumber(move.quantity)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-xs text-gray-500">Put-away plan unavailable ({entry.plan?.reason || 'unavailable'})</p>
                    )}
                  </div>
                </div>
                {(entry.plan?.reply || governance.length > 0) && (
                  <details className="border-t px-4 py-3 text-xs">
                    <summary className="cursor-pointer font-medium text-gray-700">KIP's reasoning</summary>
                    {entry.plan?.reply && <p className="mt-2 whitespace-pre-wrap text-gray-600">{entry.plan.reply}</p>}
                    {governance.length > 0 && <p className="mt-2 text-gray-500">Governance cited: {governance.join(' · ')}</p>}
                  </details>
                )}
              </div>
            );
          })
        )}
        <p className="text-xs text-gray-500">Recommendations only. Confirm capacity for any location without a profile; human approval required before executing in ShipHero.</p>
      </div>
    </ReportTemplate>
  );
};

export default InboundGetReadyReport;
